import { MaintenanceWindow } from 'model/maintenance';
import { dayJsDate } from './date';

/**
 * Internal maintenance window state
 */
const maintenanceWindow: MaintenanceWindow = {
  startDate: null,
  endDate: null,
};

/**
 * Callbacks to notify when maintenance status changes
 */
const maintenanceCallbacks: Array<(window: MaintenanceWindow) => void> = [];

/**
 * Check if we are currently in maintenance window
 */
export const isInMaintenanceWindow = (): boolean => {
  if (!maintenanceWindow.startDate || !maintenanceWindow.endDate) {
    return false;
  }

  const now = dayJsDate();
  const startDate = dayJsDate(maintenanceWindow.startDate);
  const endDate = dayJsDate(maintenanceWindow.endDate);

  return now >= startDate && now <= endDate;
};

/**
 * Subscribe to maintenance status changes
 * @returns Unsubscribe function
 */
export const onMaintenanceStatusChange = (callback: (window: MaintenanceWindow) => void) => {
  maintenanceCallbacks.push(callback);
  callback({ ...maintenanceWindow });

  return () => {
    const index = maintenanceCallbacks.indexOf(callback);
    if (index > -1) {
      maintenanceCallbacks.splice(index, 1);
    }
  };
};

/**
 * Check if maintenance is scheduled for the future
 */
export const isMaintenanceScheduled = (): boolean => {
  if (!maintenanceWindow.startDate) {
    return false;
  }

  const now = dayJsDate();
  const startDate = dayJsDate(maintenanceWindow.startDate);

  return now < startDate;
};

/**
 * Get the current maintenance window
 */
export const getMaintenanceWindow = (): MaintenanceWindow => ({
  ...maintenanceWindow,
});

/**
 * Update maintenance window from response headers
 * Internal function used by API interceptors
 */
export const updateMaintenanceWindow = (
  startDate: string | null,
  endDate: string | null,
) => {
  const windowChanged = maintenanceWindow.startDate !== startDate
    || maintenanceWindow.endDate !== endDate;

  if (!windowChanged) {
    return;
  }

  maintenanceWindow.startDate = startDate;
  maintenanceWindow.endDate = endDate;

  maintenanceCallbacks.forEach((callback) => callback({ ...maintenanceWindow }));
};
